import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AgPanel from './components/AgPanel';
import AirQuality from './components/AirQuality';
import AlertsBanner from './components/AlertsBanner';
import AstroTonight from './components/AstroTonight';
import CurrentConditions from './components/CurrentConditions';
import DailyForecast from './components/DailyForecast';
import DayPanel from './components/DayPanel';
import FarmSwitcher from './components/FarmSwitcher';
import HourlyStrip from './components/HourlyStrip';
import MinutelyPrecip from './components/MinutelyPrecip';
import MoonWeek from './components/MoonWeek';
import WindCompass from './components/WindCompass';
import PlayableWindow from './components/PlayableWindow';
import RadarMap from './components/RadarMap';
import WindMap from './components/WindMap';
import { fetchAg, fetchAir, fetchAlerts, fetchWeather } from './lib/api';
import { DEFAULT_FARMS, persistFarm, resolveInitialFarm } from './lib/farmSelection';
import { backgroundFor } from './lib/theme';

// Shared weather (radar/wind map) is centered on the olive farm and never
// follows the farm switcher (D-03).
const SHARED_WEATHER_CENTER = { lat: 33.258686, lon: -117.020067 };

export default function App() {
  const [farmId, setFarmId] = useState(resolveInitialFarm);

  function selectFarm(id: string) {
    setFarmId(id);
    persistFarm(id);
  }

  const weather = useQuery({
    queryKey: ['weather', farmId],
    queryFn: () => fetchWeather(farmId),
    refetchInterval: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
  const alerts = useQuery({
    queryKey: ['alerts'],
    queryFn: fetchAlerts,
    refetchInterval: 60 * 1000,
  });
  const ag = useQuery({
    queryKey: ['ag', farmId],
    queryFn: () => fetchAg(farmId),
    refetchInterval: 30 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
  const air = useQuery({
    queryKey: ['air'],
    queryFn: fetchAir,
    refetchInterval: 15 * 60 * 1000,
  });

  // The server fell back from an unknown ?farm= id — sync local state + URL
  // to what was actually served.
  useEffect(() => {
    if (ag.data && !ag.isPlaceholderData && ag.data.farm.id !== farmId) {
      selectFarm(ag.data.farm.id);
    }
  }, [ag.data, ag.isPlaceholderData, farmId]);

  if (weather.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        Loading…
      </div>
    );
  }
  if (weather.error || !weather.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center space-y-2">
          <div>Could not load weather.</div>
          <div className="text-xs opacity-60">{String(weather.error ?? 'unknown error')}</div>
        </div>
      </div>
    );
  }

  const w = weather.data;
  const bg = backgroundFor(w.current?.condition ?? w.daily[0]?.condition ?? '');
  const updated = w.current?.observedAt
    ? new Date(w.current.observedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '—';

  return (
    <div className={`min-h-screen ${bg} text-white transition-colors duration-700`}>
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <header className="space-y-2">
          <div className="flex items-baseline justify-between">
            <h1 className="text-2xl font-light tracking-wide">{w.location.name}</h1>
            <div className="flex items-baseline gap-3 text-xs opacity-70">
              <span>Updated {updated}</span>
              <a
                href={`/admin?tab=farms&farm=${encodeURIComponent(farmId)}`}
                className="hover:opacity-100 hover:underline underline-offset-2"
              >
                Admin
              </a>
            </div>
          </div>
          {/* Farm tabs up top so the foliage farm is reachable without scrolling;
              the same switcher also appears on the farm panel further down. */}
          <FarmSwitcher
            farms={ag.data?.farms ?? DEFAULT_FARMS}
            selected={farmId}
            onSelect={selectFarm}
          />
        </header>

        {alerts.data?.alerts?.length ? <AlertsBanner alerts={alerts.data.alerts} /> : null}

        <CurrentConditions current={w.current} today={w.daily[0]} />
        {w.day && <DayPanel day={w.day} timeZone={w.location.timeZone} />}
        <WindCompass current={w.current} />
        <MinutelyPrecip minutely={w.minutely} nearestStorm={w.nearestStorm} />
        {air.data && <AirQuality air={air.data} />}
        <HourlyStrip hourly={w.hourly} timeZone={w.location.timeZone} />
        <DailyForecast days={w.daily} />
        {w.moonWeek.length > 0 && <MoonWeek week={w.moonWeek} timeZone={w.location.timeZone} />}
        {w.day && (
          <AstroTonight
            day={w.day}
            hourly={w.hourly}
            timeZone={w.location.timeZone}
            sunsetQuality={w.sunsetQuality}
            planets={w.planets}
          />
        )}
        <PlayableWindow
          hourly={w.hourly}
          timeZone={w.location.timeZone}
          rainPast48hIn={w.rainPast48hIn}
        />
        <AgPanel
          ag={ag.data ?? null}
          loading={ag.isLoading}
          farmName={
            (ag.data?.farms ?? DEFAULT_FARMS).find((f) => f.id === farmId)?.name ?? 'Farm'
          }
          switcher={
            <FarmSwitcher
              farms={ag.data?.farms ?? DEFAULT_FARMS}
              selected={farmId}
              onSelect={selectFarm}
            />
          }
        />
        <RadarMap
          centerLat={SHARED_WEATHER_CENTER.lat}
          centerLon={SHARED_WEATHER_CENTER.lon}
          sunrise={w.day?.sunrise ?? null}
          sunset={w.day?.sunset ?? null}
        />
        <WindMap centerLat={SHARED_WEATHER_CENTER.lat} centerLon={SHARED_WEATHER_CENTER.lon} />

        <footer className="text-xs opacity-60 text-center pt-8 pb-4">
          Forecast: NOAA / NWS · Radar: RainViewer
        </footer>
      </main>
    </div>
  );
}
